<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { useAnalysisStore } from '@/store/modules/analysis';
import {
  removeAsterisk,
  splitKeywordParts,
  splitBySentence,
  splitSentencesByDivider,
  splitCommaSeparatedWords,
} from '@/composables/splitSentence';
import PersonalPieChart from './PersonalPieChart.vue';
import PersonalityKeyword from './PersonalityKeyword.vue';

const props = defineProps({
  pData: Object,
  userName: String,
  isMobile: Boolean,
});
const store = useAnalysisStore();
// const userName = computed(() => store.userName)
onMounted(() => {
  // pData.value = store.analysisResult;
});

const dataGathering = computed(() => splitBySentence(props.pData.정보수집.기본특성));
const infoGathering = computed(() => splitBySentence(props.pData.정보수집.상세특성));
const strengthItems = computed(() => splitCommaSeparatedWords(props.pData.정보수집.주요강점));
const weaknessItems = computed(() => splitCommaSeparatedWords(props.pData.정보수집.주요약점));
const strengthDetail = computed(() => splitBySentence(props.pData.정보수집.강점설명));
const weaknessDetail = computed(() => splitBySentence(props.pData.정보수집.약점설명));
const strengthAdvice = computed(() => props.pData.정보수집.강점조언);
const weaknessAdvice = computed(() => props.pData.정보수집.약점조언);

const nCodeData = computed(() => props.pData.낭만성향.성향분포.NCODE_RTOS['ns_rto']);
const parsedNS = computed(() => {
  const type = nCodeData.value[0];
  const value = Number(nCodeData.value[1]) || 0;
  let n = 0;
  let s = 0;
  if (type === 'N') {
    n = value;
    s = 100 - n;
  } else if (type === 'S') {
    s = value;
    n = 100 - s;
  }
  return { n, s };
});
const eiChartData = computed(() => ({
  'N (직관적 성향)': parsedNS.value.n.toFixed(1),
  'S (감각적 성향)': parsedNS.value.s.toFixed(1),
}));
const eiLabelList = ['N (직관적 성향)', 'S (감각적 성향)'];
const eiColorMap = {
  'N (직관적 성향)': '#ADCC32',
  'S (감각적 성향)': '#7D67FD',
};
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title personality">
          <span class="detail-tit__num personality">6</span>
          <span class="detail-tit__txt personality">
            <span>정보수집의 관점에서 보는&nbsp;</span>
            <span>나의 스타일은?</span>
          </span>
        </div>
        <div class="personality-wrapper">
          <div class="person-content-box">
            <div class="personality-chart__wrapper">
              <PersonalPieChart
                :chartData="eiChartData"
                :labelList="eiLabelList"
                :colorMap="eiColorMap"
                centerTitle="정보수집"
                labelDirection="row"
              />
            </div>
          </div>
          <div class="personality-divider" />
          <div class="person-content-box">
            <PersonalityKeyword
              :left-title="['정보를 대하는', '나의 성향 키워드 😊']"
              :keywords="props.pData.정보수집.주요특징"
              :isMobile="isMobile"
            />
          </div>
          <div class="personality-divider" />

          <div class="person-content-box">
            <div class="content__title">🖥️ 내가 정보를 받아들이는 스타일은?</div>
            <div v-for="(sentence, idx) in dataGathering" :key="idx">
              <span class="content__body-txt">
                {{ sentence }}
              </span>
            </div>
          </div>
          <div class="personality-divider" />
          <div class="person-content-box">
            <div class="content__title">😋 조금 더 들어가 볼까요?</div>
            <div v-for="(sentence, idx) in infoGathering" :key="idx">
              <span class="content__body-txt">
                {{ sentence }}
              </span>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="personality-wrapper">
          <div class="person-desc-box">
            <div class="desc__title">💪 이런 키워드가 나의 강점을 말해줘요.</div>
            <div class="desc__body blue">
              <div class="top-keyword">
                <div class="top-keyword__box">
                  <div v-for="(item, idx) in strengthItems" :key="idx" class="keyword-item blue">
                    {{ item }}
                  </div>
                </div>
                <div v-for="(str, idx) in strengthDetail" :key="idx" class="top-keyword__desc">
                  {{ str }}
                </div>
              </div>
              <div class="bottom-white-box">
                <div class="white-box__tit">나를 돋보이게 만들어주는 환경들 😎</div>
                <div class="white-box__desc">
                  {{ strengthAdvice }}
                </div>
              </div>
            </div>
          </div>
          <div class="person-desc-box">
            <div class="desc__title">👀 조금 더 살펴보세요.</div>
            <div class="desc__body red">
              <div class="top-keyword">
                <div class="top-keyword__box">
                  <div v-for="(item, idx) in weaknessItems" :key="idx" class="keyword-item red">
                    {{ item }}
                  </div>
                </div>
                <div v-for="(str, idx) in weaknessDetail" :key="idx" class="top-keyword__desc">
                  {{ str }}
                </div>
              </div>
              <div class="bottom-white-box">
                <div class="white-box__tit">나를 위한 작은 연습 😌</div>
                <div class="white-box__desc">
                  {{ weaknessAdvice }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
